import React from "react";

/**
 * One colour rule for performance ratings, shared by every rating dropdown and
 * badge so a rating looks the same wherever it appears.
 *
 * The rating type's own flags decide: a debarring rating (Suspended, Globally
 * Suspended, Globally Blacklisted…) is danger, a flagging one (Not
 * Satisfactory) is a warning. The names are only a fallback for data that
 * arrives without its flags, so a new debarring type added under Data Types is
 * red without a code change.
 */

export type RatingTone = "success" | "warning" | "danger" | "info";

type RatingType = {
  name?: string;
  isFlag?: boolean | null;
  isDebarred?: boolean | null;
};

export const ratingTone = (type?: RatingType | string | null): RatingTone => {
  const rating: RatingType = typeof type === "string" ? { name: type } : type || {};
  if (rating.isDebarred) return "danger";
  if (rating.isFlag) return "warning";

  const name = (rating.name || "").trim().toLowerCase();
  if (/suspend|blacklist|debar/.test(name)) return "danger";
  if (name === "not satisfactory") return "warning";
  if (name === "satisfactory") return "success";
  return "info";
};

export type RatingTypeOption = { value: any; label: string; tone: RatingTone };

/** A react-select option for a rating type, carrying its colour. */
export const ratingTypeOption = (type: any): RatingTypeOption => ({
  value: type?.id,
  label: type?.name || "",
  tone: ratingTone(type),
});

/** Option label for rating dropdowns: the rating's colour dot, then its name. */
export const RatingOptionLabel = (option: { label: string; tone?: RatingTone }) => (
  <span className="d-inline-flex align-items-center gap-2">
    <span
      className={`rating-dot is-${option.tone || ratingTone(option.label)}`}
      aria-hidden="true"
    ></span>
    {option.label}
  </span>
);
